<template>
	<section class="otl-plots">
		<StatsChartTitleWithDescription titleKey="otl-plots.header" descriptionKey="otl-plots.description" titleClass="" descriptionClass="" />
		<TypographyText type="secondary">
			{{ $t('otl-plots.blqUpdatedLabel') }}
			<DateTimeDisplay :date="otlBlqUpdated" format="YYYY-MM-DD HH:mm:ss" />
		</TypographyText>
		<Row :gutter="[16, 8]" :class="{ 'otl-plots-row-small': smallScreen }">
			<Col :span="smallScreen ? 24 : 18">
				<div class="otl-plot-frame">
					<Spin v-if="isLoading" :spinning="true" />
					<img v-else-if="displayPlot?.image" :src="displayPlot.image" :alt="$t('otl-plots.imageAlt', { period: displayPlot.period })" width="1200" height="840" />
					<Empty v-else :image="simpleImage" :description="$t('otl-plots.noImage')" />
				</div>
			</Col>
			<Col :span="smallScreen ? 24 : 6" class="otl-plot-controls">
				<Card :bordered="false">
					{{ $t('otl-plots.periodLabel') }}
					<Select v-model:value="periodDays" class="width-100">
						<SelectOption v-for="days in periodOptions" :key="days" :value="days">{{ $t('otl-plots.periods.' + days) }}</SelectOption>
					</Select>
				</Card>
			</Col>
		</Row>
		<SettingsInfoBanner v-if="hasWarning" status="warning" class="pd-md-vertical">
			{{ $t('otl-plots.updateFailed') }}
		</SettingsInfoBanner>
	</section>
</template>

<script lang="ts">
import { computed, ref, watch } from 'vue';
import { Card, Col, Empty, Row, Select, SelectOption, Spin, TypographyText } from 'ant-design-vue';

import ApiOtl from '@/api/ApiOtl';
import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import { useSmallScreen } from '@/utils/useSmallScreen';

const periodOptions = [1, 3, 5, 7, 14, 28, 100];
type OtlPlot = { period: string; image: string | null; updated: boolean };

function isOtlPlot(value: unknown): value is OtlPlot {
	return typeof value === 'object'
		&& value !== null
		&& 'period' in value && typeof value.period === 'string'
		&& 'image' in value && (typeof value.image === 'string' || value.image === null)
		&& 'updated' in value && typeof value.updated === 'boolean';
}

export default {
	components: {
		Card, Col, DateTimeDisplay, Empty, Row, Select, SelectOption, Spin, TypographyText,
		SettingsInfoBanner, StatsChartTitleWithDescription,
	},
	props: {
		baseStationId: {
			type: Number,
			required: true,
		},
		otlBlqUpdated: {
			type: [String, null],
			default: null,
		},
	},
	setup(props) {
		const { smallScreen } = useSmallScreen();
		const { apiData, isLoading, doRequest, resetData } = ApiOtl.useOtlPlotsGet();
		const periodDays = ref(7);
		const requested = ref(false);
		watch([() => props.baseStationId, periodDays], ([id, days]) => {
			resetData();
			requested.value = true;
			doRequest({ id, period_days: days });
		}, { immediate: true });
		const displayPlot = computed<OtlPlot | null>(() => {
			const plot = apiData.value.plot;
			return isOtlPlot(plot) && plot.period === periodDays.value * 24 + 'h' ? plot : null;
		});
		const hasWarning = computed(() => requested.value && !isLoading.value && !displayPlot.value?.updated);
		return {
			smallScreen,
			isLoading,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
			periodDays,
			periodOptions,
			displayPlot,
			hasWarning,
		};
	},
};
</script>

<style scoped>
.otl-plots-row-small .otl-plot-controls {
	order: -1;
}
.otl-plot-frame {
	width: min(100%, 1200px);
	aspect-ratio: 10 / 7;
	display: flex;
	align-items: center;
	justify-content: center;
}
.otl-plot-frame img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}
</style>
